import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Minus, Plus, ShoppingBag, Truck, ShieldCheck, Leaf, Star, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

// If backend returns relative /api/static/... URLs, prefix them with backend origin
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(null);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImg(abs(r.data.image_url));
    }).catch(() => {});
  }, [slug]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const list = [abs(product.image_url), ...(product.gallery || []).map(abs)].filter(Boolean);
    return Array.from(new Set(list));
  }, [product]);

  if (!product) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-[#666]">Loading...</div></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16" data-testid={`product-page-${slug}`}>
      <nav className="text-sm text-[#666] mb-6">
        <Link to="/" className="hover:text-[#E65A28]">Home</Link> / <Link to="/shop" className="hover:text-[#E65A28]">Shop</Link> / <span className="text-[#0A192F]">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="aspect-square rounded-3xl overflow-hidden bg-[#F4EBE1] flex items-center justify-center">
            <img src={activeImg} alt={product.name} className="w-full h-full object-contain p-6 md:p-12" data-testid="product-main-image" />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-4 md:grid-cols-5 gap-3">
              {gallery.map((g, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(g)}
                  className={`aspect-square rounded-xl overflow-hidden bg-[#F4EBE1] flex items-center justify-center border-2 transition-colors ${activeImg === g ? "border-[#E65A28]" : "border-transparent hover:border-[#0A192F]/20"}`}
                  data-testid={`product-thumb-${idx}`}
                >
                  <img src={g} alt="" className="w-full h-full object-contain p-2" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E65A28] mb-3 font-semibold">{product.category_slug.replace("-", " ")}</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0A192F] leading-tight">{product.name}</h1>

          <div className="flex items-center gap-2 mt-3">
            <div className="flex">
              {[1,2,3,4,5].map(i => <Star key={i} className={`h-4 w-4 ${i <= Math.round(product.rating) ? "fill-[#E65A28] text-[#E65A28]" : "text-[#ccc]"}`} />)}
            </div>
            <span className="text-sm text-[#666]">{product.rating} ({product.review_count} reviews)</span>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-3xl font-semibold text-[#0A192F]">₹{product.price}</span>
            {product.compare_at_price && (
              <>
                <span className="text-lg text-[#666] line-through">₹{product.compare_at_price}</span>
                <span className="text-sm text-[#E65A28] font-semibold">
                  {Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)}% off
                </span>
              </>
            )}
          </div>

          <p className="mt-6 text-[#333] leading-relaxed">{product.short_description}</p>

          {(product.benefits || []).length > 0 && (
            <ul className="mt-5 space-y-2">
              {product.benefits.slice(0, 4).map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#0A192F]">
                  <CheckCircle2 className="h-4 w-4 text-[#E65A28] mt-0.5 flex-shrink-0" /> {b}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-[#0A192F]/20 rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#F4EBE1] rounded-l-full" data-testid="qty-decrease"><Minus className="h-4 w-4" /></button>
              <span className="px-4 min-w-[3rem] text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#F4EBE1] rounded-r-full" data-testid="qty-increase"><Plus className="h-4 w-4" /></button>
            </div>
            <button
              onClick={() => add(product, qty)}
              className="flex-1 bg-[#E65A28] hover:bg-[#c9481c] text-white px-6 py-4 rounded-full font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform"
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag className="h-4 w-4" /> Add to cart
            </button>
          </div>
          <button
            onClick={() => { add(product, qty); navigate("/checkout"); }}
            className="mt-3 w-full bg-[#0A192F] hover:bg-black text-white px-6 py-4 rounded-full font-semibold hover:-translate-y-0.5 transition-transform"
            data-testid="buy-now-btn"
          >
            Buy it now
          </button>

          <div className="mt-8 grid grid-cols-3 gap-3 text-center">
            <div className="bg-[#F4EBE1]/60 rounded-xl p-4">
              <Truck className="h-5 w-5 text-[#E65A28] mx-auto" />
              <p className="text-xs mt-2 text-[#0A192F] font-medium">Free shipping ₹499+</p>
            </div>
            <div className="bg-[#F4EBE1]/60 rounded-xl p-4">
              <Leaf className="h-5 w-5 text-[#E65A28] mx-auto" />
              <p className="text-xs mt-2 text-[#0A192F] font-medium">100% Ayurvedic</p>
            </div>
            <div className="bg-[#F4EBE1]/60 rounded-xl p-4">
              <ShieldCheck className="h-5 w-5 text-[#E65A28] mx-auto" />
              <p className="text-xs mt-2 text-[#0A192F] font-medium">Derm-tested</p>
            </div>
          </div>
        </div>
      </div>

      {/* Benefit highlights strip */}
      {(product.benefit_highlights || []).length > 0 && (
        <section className="mt-16 md:mt-24">
          <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-3 font-semibold text-center">Why you&apos;ll love it</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0A192F] text-center mb-10">The Roop Anand difference</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {product.benefit_highlights.map((bh, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden hover-lift">
                {bh.image && <div className="aspect-square bg-[#F4EBE1]"><img src={abs(bh.image)} alt={bh.title || ""} className="w-full h-full object-cover" /></div>}
                <div className="p-5">
                  <h4 className="font-serif text-xl text-[#0A192F]">{bh.title}</h4>
                  <p className="text-sm text-[#666] mt-2 leading-relaxed">{bh.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Accordion type="single" collapsible className="mt-12 max-w-3xl mx-auto">
        <AccordionItem value="desc">
          <AccordionTrigger data-testid="acc-description">Description</AccordionTrigger>
          <AccordionContent className="text-[#333] leading-relaxed">{product.description}</AccordionContent>
        </AccordionItem>
        <AccordionItem value="ing">
          <AccordionTrigger data-testid="acc-ingredients">Ingredients</AccordionTrigger>
          <AccordionContent className="text-[#333]">{product.ingredients}</AccordionContent>
        </AccordionItem>
        <AccordionItem value="use">
          <AccordionTrigger data-testid="acc-how-to-use">How to use</AccordionTrigger>
          <AccordionContent className="text-[#333]">{product.how_to_use}</AccordionContent>
        </AccordionItem>
        <AccordionItem value="ben">
          <AccordionTrigger data-testid="acc-benefits">Benefits</AccordionTrigger>
          <AccordionContent>
            <ul className="list-disc list-inside space-y-1 text-[#333]">
              {(product.benefits || []).map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
